import { parseNumber } from '@/utils/number';
import { moveMoneyApi, type MoveMoneyRequest } from '@/api/moveMoney';
import { usePost } from '@foodics/ui-common';
import { createSharedComposable } from '@vueuse/core';
import { reactive, ref, toRefs, watch } from 'vue';
import type { Beneficiary } from '@/types/models';
import {
    getDay,
    isBefore,
    isSameDay,
    startOfToday,
    addDays,
    isAfter,
} from 'date-fns';

export type MoneyMovementType = 'transfer' | 'send';
export const MOVE_MONEY_UPPER_LIMIT = 75000;

export const useMoveMoney = createSharedComposable(() => {
    const moneyMovementType = ref<MoneyMovementType>('send');
    const reference = ref();
    const formValues = reactive({
        fromBankAccountId: '',
        toBankAccountId: '',
        amount: '',
        beneficiary: undefined as Beneficiary | undefined,
        invoiceId: undefined as string | undefined,
        selectedTiming: 'immediately' as 'immediately' | 'schedule',
        scheduledAt: undefined as string | undefined,
    });

    const hasCheckedApprovalRule = ref(false);
    const hasApprovalRuleRequired = ref(false);
    watch(
        () => formValues.amount,
        () => {
            hasApprovalRuleRequired.value = false;
            hasCheckedApprovalRule.value = false;
        },
    );

    // Reset scheduledAt when selectedTiming changes to immediately
    watch(
        () => formValues.selectedTiming,
        () => {
            if (formValues.selectedTiming === 'immediately') {
                formValues.scheduledAt = undefined;
            }
        },
    );

    const resetBeneficiaryInfo = () => {
        formValues.beneficiary = undefined;
        formValues.scheduledAt = undefined;
        formValues.selectedTiming = 'immediately';
    };

    const reset = () => {
        moneyMovementType.value = 'send';
        hasApprovalRuleRequired.value = false;
        hasCheckedApprovalRule.value = false;
        formValues.fromBankAccountId = '';
        formValues.toBankAccountId = '';
        formValues.amount = '';
        formValues.invoiceId = undefined;
        formValues.scheduledAt = undefined;
        formValues.selectedTiming = 'immediately';
        resetBeneficiaryInfo();
    };

    const {
        execute: executeMoveMoney,
        isLoading: isMovingMoney,
        error: moveMoneyError,
        onData: onMoveMoneyRequestFulfilled,
    } = usePost<MoveMoneyRequest['payload'], MoveMoneyRequest['response']>(
        moveMoneyApi.move,
    );

    async function moveMoney(otp: string) {
        const payload: Partial<MoveMoneyRequest['payload']> = {
            amount: parseNumber(formValues.amount),
            currency: 'SAR',
            source_bank_account_id: formValues.fromBankAccountId,
            otp_token: otp,
        };

        if (moneyMovementType.value === 'transfer') {
            payload.destination_bank_account_id = formValues.toBankAccountId;
        }

        if (moneyMovementType.value === 'send') {
            payload.beneficiary_id = formValues.beneficiary?.id;
            if (formValues.invoiceId) {
                payload.invoice_id = formValues.invoiceId;
            }
        }

        if (formValues.scheduledAt) {
            payload.submit_at = formValues.scheduledAt;
        }

        const { data } = await executeMoveMoney(payload);
        reference.value = data?.data.provider_reference ?? '';
    }

    return {
        ...toRefs(formValues),
        moneyMovementType,
        reset,
        moveMoney,
        isMovingMoney,
        moveMoneyError,
        onMoveMoneyRequestFulfilled,
        hasApprovalRuleRequired,
        hasCheckedApprovalRule,
        reference,
    };
});

export function isInvalidScheduledDate(date: Date) {
    try {
        const today = startOfToday();
        const day = getDay(date);
        const isFriday = day === 5;
        const isSaturday = day === 6;
        const isToday = isSameDay(date, today);
        const isPast = isBefore(date, today);
        const maxAllowedDate = addDays(today, 60);
        const isMoreThan60Days = isAfter(date, maxAllowedDate);

        return isPast || isFriday || isSaturday || isToday || isMoreThan60Days;
    } catch {
        return true;
    }
}
